import {AfterViewInit, Component, OnDestroy, OnInit} from '@angular/core';
import { File, Level, BreadcrumbElement } from '@app/models/content';
import { ToolsService, ContentService, EventsService, TrackingService } from '@app/services/services';
import { TranslateService } from '@ngx-translate/core';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { Subscription } from 'rxjs';
import { Topic, TopicVal } from '@app/shared/enums';
import { MetatagsService } from '@app/services/metatags/metatags.service';
import { environment } from '@env/environment';

@Component({
  selector: 'app-file',
  templateUrl: './file.component.html',
  styleUrls: ['./file.component.scss']
})
export class FileComponent implements OnInit, AfterViewInit, OnDestroy {
  file: File;
  levels: Level[];
  active = false;
  subscriptions: Subscription[] = [];
  breadcrumbTopicRoute: string;
  viewInit = false;
  fragmentToScroll: string;
  reversedTopic: string;
  breadcrumbElements: BreadcrumbElement[] = [];
  isTopicRevision = false;
  isOtherTopic = false;



  constructor(
    private tools: ToolsService,
    private content: ContentService,
    private events: EventsService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private tracking: TrackingService,
    private metaTagsService: MetatagsService
  ) {}

  ngOnInit() {
    this.tools.addBodyClass('page__fiche');
    this.subscriptions.push(
      this.route.paramMap.subscribe((params: ParamMap) => {
        const slug = params.get('id');
        const urlParts = slug.split('-');
        const code = urlParts[urlParts.length - 1];
        this.reversedTopic = this.translate.instant(
          `Common.Library.ReverseRoutes.${params.get('topic')}`
        );


        this.isOtherTopic = (this.reversedTopic === Topic.others);
        const isEnglish = ( this.reversedTopic === Topic.english);

        // Todo: english test only there in case of going live in urgency
        if ( environment.production && isEnglish ) {
          this.redirectTo404();
        }

        // IF Topic doesn't exist then tedirect to 404
        if (
          !TopicVal.hasOwnProperty(this.reversedTopic) &&
          !this.reversedTopic.startsWith('revision')
        ) {
          this.redirectTo404();
        }

        this.getFile(code);
      })
    );
  }

  ngAfterViewInit(): void {
    this.viewInit = true;
    if (this.fragmentToScroll) {
      this.scrollToFragment();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.tools.removeBodyClass('page__fiche');
  }

  onClick() {
    this.active = !this.active;
  }

  getFile(code: string) {
    this.events.broadcast('loading', true);
    this.subscriptions.push(
      this.content.getFile(this.translate.currentLang, code).subscribe(
        result => {
          if (result.errors) {
            this.redirectTo404();
          } else {
            // Todo: english test only there in case of going live in urgency
            if (environment.production && result.data.file.topic === Topic.english) {
              this.redirectTo404();
            }
            const notSameTopic = this.reversedTopic !== result.data.file.topic;
            const beginWithRevision = this.reversedTopic.startsWith('revision');

            if ( notSameTopic && !beginWithRevision ) {
              this.redirectTo404();
            // Else we display the file
            } else {
              this.file = result.data.file;
              this.isTopicRevision = this.file.topic.startsWith('revision');
              this.levels = this.content.getLevelsFile(this.file.levels);
              this.getBreadcrumbElements();
              this.breadcrumbTopicRoute = '/bv/' + (
                this.isTopicRevision ? 'revisions' : this.tools.getTopicVal(this.file.topic)
              );
              this.tools.setTheme(this.isTopicRevision ? 'default' : this.file.topic);
              this.events.broadcast('loading', false);
              this.getRouteFragment();
              if (this.file.metatags) {
                this.metaTagsService.updateMetaTags(this.file.metatags);
              }
              this.file.id = result.data.file.uuid;
              this.tracking.pushVar({
                event: 'hit',
                content_type: 'file',
                content_id: this.file.id
              });
            }
          }
        },
        error => {
          this.redirectTo404();
        }
      )
    );
  }

  getRouteFragment() {
    this.subscriptions.push(
      this.route.fragment.subscribe((fragment: string) => {
        if (fragment) {
          this.fragmentToScroll = fragment;
          if (this.viewInit) {
            this.scrollToFragment();
          }
        }
      })
    );
  }

  getBreadcrumbElements() {
    if (Array.isArray(this.file.children) && this.file.children.length) {
      this.file.children.forEach(child => {
        const childBreadcrumbTopicRoute = '/bv/' +
        (this.isTopicRevision ? 'revisions' : this.tools.getTopicVal(child.topic)
        + '/' + child.slug + '-' + child.code);
        this.breadcrumbElements.push({routerLink: childBreadcrumbTopicRoute, title: child.title.toString()} as BreadcrumbElement);
      });
    }
  }

  scrollToFragment() {
    setTimeout(
      () => {
        let moduleEl = null;
        let moduleSlug = null;
        let entitySlug = null;

        if (this.fragmentToScroll.startsWith('video')) {
          moduleEl = this.tools.document.querySelector(
            `#${this.fragmentToScroll.split('--')[1]}`
          );
        } else if (this.fragmentToScroll.startsWith('exercise')) {
          [moduleSlug, entitySlug] = this.fragmentToScroll.split('--')[1].split('__');
          moduleEl = this.tools.document.querySelector(`#${moduleSlug}`);
        }

        if (moduleEl) {
          const calculatedTop =
            window.scrollY +
            moduleEl.getBoundingClientRect().top -
            this.tools.fileModuleScrollOffset;

          if (calculatedTop > 0) {
            this.tools.scrollTo({
              top: calculatedTop
            });

            if (moduleSlug && entitySlug) {
              this.events.broadcast(`exercise--${moduleSlug}:navigate-to`, entitySlug);
            }
          }
        }
        this.fragmentToScroll = null;
      },
      900
    );
  }

  redirectTo404() {
    this.events.broadcast('loading', false);
    this.router.navigate(['/404'], {replaceUrl: true});
  }
}
